import {
	Bot,
	ChevronDown,
	ChevronUp,
	ExternalLink,
	Fish,
	Loader2,
	Maximize2,
	MessageSquare,
	Reply,
	Send,
	ShieldCheck,
	SmilePlus,
	Trash2,
	X,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AI_CODE_REVIEWER_USERNAME } from "../constants";
import { useMRNotes } from "../hooks/useMRNotes";
import { safeHttpUrl } from "../utils";
import { Avatar } from "./Avatar";
import { DateTime } from "./DateTime";
import { Tooltip } from "./Tooltip";

const MODAL_EXIT_MS = 120;
const EMOJI_REACTION_PRESETS = [
	{ name: "thumbsup", emoji: "👍", label: "Thumbs up" },
	{ name: "heart", emoji: "❤️", label: "Heart" },
	{ name: "tada", emoji: "🎉", label: "Celebrate" },
	{ name: "eyes", emoji: "👀", label: "Eyes" },
	{ name: "rocket", emoji: "🚀", label: "Rocket" },
	{ name: "laughing", emoji: "😄", label: "Laugh" },
];
const EMOJI_BY_NAME = Object.fromEntries(
	EMOJI_REACTION_PRESETS.map((reaction) => [reaction.name, reaction.emoji]),
);

const INLINE_MARKDOWN_PATTERN =
	/(`[^`\n]+`|\[([^\]\n]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)|\*\*([^*\n]+)\*\*|\*([^*\n]+)\*|\bhttps?:\/\/[^\s<]+[^\s<.,;:!?)]?)/g;

const getCodeLanguage = (infoString) => {
	const display = String(infoString || "")
		.trim()
		.split(/\s+/)[0]
		.replace(/[^\w#+.-]/g, "")
		.slice(0, 24)
		.toLowerCase();
	if (!display) return null;

	return {
		className: display.replace(/[^a-z0-9_-]/g, "-"),
		display,
	};
};

const getCommentUrl = (note, mrWebUrl) => {
	const noteUrl = safeHttpUrl(note?.url);
	if (noteUrl) return noteUrl;

	const fallbackUrl =
		note?.restId && mrWebUrl ? `${mrWebUrl}#note_${note.restId}` : "";
	return safeHttpUrl(fallbackUrl);
};

const stopDisabledCommentLinkClick = (event) => {
	const target = event.target;
	if (!(target instanceof Element)) return;
	if (target.closest("button")) return;

	const link = target.closest("a");
	if (!link) return;

	event.preventDefault();
	event.stopPropagation();
};

const consumeEscapeKey = (event) => {
	event.preventDefault();
	event.stopPropagation();
	event.stopImmediatePropagation?.();
};

const renderInlineMarkdown = (text, keyPrefix) => {
	const source = String(text || "");
	const nodes = [];
	let lastIndex = 0;
	let matchIndex = 0;

	for (const match of source.matchAll(INLINE_MARKDOWN_PATTERN)) {
		if (match.index > lastIndex) {
			nodes.push(source.slice(lastIndex, match.index));
		}

		const key = `${keyPrefix}-inline-${matchIndex}`;
		const token = match[0];
		if (token.startsWith("`")) {
			nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
		} else if (match[2] && match[3]) {
			const href = safeHttpUrl(match[3]);
			nodes.push(
				href ? (
					<a key={key} href={href} target="_blank" rel="noopener noreferrer">
						{match[2]}
					</a>
				) : (
					match[2]
				),
			);
		} else if (match[4]) {
			nodes.push(<strong key={key}>{match[4]}</strong>);
		} else if (match[5]) {
			nodes.push(<em key={key}>{match[5]}</em>);
		} else {
			const href = safeHttpUrl(token);
			nodes.push(
				href ? (
					<a key={key} href={href} target="_blank" rel="noopener noreferrer">
						{token}
					</a>
				) : (
					token
				),
			);
		}

		lastIndex = match.index + token.length;
		matchIndex += 1;
	}

	if (lastIndex < source.length) nodes.push(source.slice(lastIndex));
	return nodes;
};

const isMarkdownBlockStart = (line) =>
	/^\s*(```|#{1,4}\s+|>\s?|([-*+]|\d+[.)])\s+)/.test(line);

const renderMarkdownFallback = (body, className, disableLinks = false) => {
	const lines = String(body || "")
		.replace(/\r\n?/g, "\n")
		.split("\n");
	const blocks = [];
	let index = 0;
	let blockIndex = 0;

	while (index < lines.length) {
		const line = lines[index];
		if (!line.trim()) {
			index += 1;
			continue;
		}

		const fence = line.match(/^\s*```\s*([^\s`]*)?/);
		if (fence) {
			const language = getCodeLanguage(fence[1]);
			const codeLines = [];
			index += 1;
			while (index < lines.length && !/^\s*```/.test(lines[index])) {
				codeLines.push(lines[index]);
				index += 1;
			}
			if (index < lines.length) index += 1;
			blocks.push(
				<pre
					key={`block-${blockIndex}`}
					data-language={language?.display || undefined}
				>
					<code className={language ? `language-${language.className}` : ""}>
						{codeLines.join("\n")}
					</code>
				</pre>,
			);
			blockIndex += 1;
			continue;
		}

		const heading = line.match(/^\s*(#{1,4})\s+(.+)$/);
		if (heading) {
			const HeadingTag = `h${heading[1].length}`;
			blocks.push(
				<HeadingTag key={`block-${blockIndex}`}>
					{renderInlineMarkdown(heading[2], `block-${blockIndex}`)}
				</HeadingTag>,
			);
			blockIndex += 1;
			index += 1;
			continue;
		}

		if (/^\s*>\s?/.test(line)) {
			const quoteLines = [];
			while (index < lines.length && /^\s*>\s?/.test(lines[index])) {
				const text = lines[index].replace(/^\s*>\s?/, "");
				quoteLines.push({
					key: `block-${blockIndex}-quote-${quoteLines.length}-${text}`,
					text,
				});
				index += 1;
			}
			blocks.push(
				<blockquote key={`block-${blockIndex}`}>
					{quoteLines.map((quoteLine) => (
						<p key={quoteLine.key}>
							{renderInlineMarkdown(quoteLine.text, quoteLine.key)}
						</p>
					))}
				</blockquote>,
			);
			blockIndex += 1;
			continue;
		}

		const listStart = line.match(/^\s*(([-*+])|(\d+[.)]))\s+(.+)$/);
		if (listStart) {
			const ordered = Boolean(listStart[3]);
			const items = [];
			while (index < lines.length) {
				const item = lines[index].match(/^\s*(([-*+])|(\d+[.)]))\s+(.+)$/);
				if (!item || Boolean(item[3]) !== ordered) break;
				const task = item[4].match(/^\[([ xX])\]\s+(.+)$/);
				const text = task ? task[2] : item[4];
				items.push({
					checked: task ? task[1].toLowerCase() === "x" : null,
					key: `block-${blockIndex}-item-${items.length}-${text}`,
					text,
				});
				index += 1;
			}

			const ListTag = ordered ? "ol" : "ul";
			blocks.push(
				<ListTag key={`block-${blockIndex}`}>
					{items.map((item) => (
						<li key={item.key}>
							{item.checked !== null && (
								<input type="checkbox" checked={item.checked} readOnly />
							)}
							{renderInlineMarkdown(item.text, item.key)}
						</li>
					))}
				</ListTag>,
			);
			blockIndex += 1;
			continue;
		}

		const paragraphLines = [line.trim()];
		index += 1;
		while (
			index < lines.length &&
			lines[index].trim() &&
			!isMarkdownBlockStart(lines[index])
		) {
			paragraphLines.push(lines[index].trim());
			index += 1;
		}
		blocks.push(
			<p key={`block-${blockIndex}`}>
				{renderInlineMarkdown(paragraphLines.join(" "), `block-${blockIndex}`)}
			</p>,
		);
		blockIndex += 1;
	}

	return (
		<div
			className={`rb-comment-html rb-comment-markdown ${className || ""}`}
			onClickCapture={disableLinks ? stopDisabledCommentLinkClick : undefined}
		>
			{blocks}
		</div>
	);
};

// Renders a comment body: prefers GitLab's rendered HTML (full markdown
// formatting), falls back to plain text if bodyHtml is unavailable.
export const CommentBody = ({ note, className, disableLinks = false }) => {
	if (note.bodyHtml) {
		return (
			<div
				className={`rb-comment-html ${className || ""}`}
				onClickCapture={disableLinks ? stopDisabledCommentLinkClick : undefined}
				// GitLab sanitizes bodyHtml server-side before returning it.
				// biome-ignore lint/security/noDangerouslySetInnerHtml: GitLab returns sanitized rendered markdown.
				dangerouslySetInnerHTML={{ __html: note.bodyHtml }}
			/>
		);
	}
	return renderMarkdownFallback(note.body, className, disableLinks);
};

export const EmojiReactionBar = ({
	note,
	onReact,
	onUnreact,
	canReact = true,
	reactingKey = null,
	compact = false,
	className = "",
	onError,
}) => {
	const [localError, setLocalError] = useState(null);
	const [open, setOpen] = useState(false);
	const [position, setPosition] = useState({ top: 0, left: 0 });
	const triggerRef = useRef(null);
	const menuRef = useRef(null);
	const awards = Array.isArray(note?.awardEmojis) ? note.awardEmojis : [];
	const awardsByName = new Map(awards.map((award) => [award.name, award]));
	const appliedAwards = awards.filter(
		(award) => (award?.count || 0) > 0 || award?.currentUserReacted,
	);
	const dropdownNames = [
		...new Set([
			...EMOJI_REACTION_PRESETS.map((reaction) => reaction.name),
			...awards.map((award) => award.name),
		]),
	];
	const canOpenDropdown = canReact && Boolean(onReact || onUnreact);

	const updatePosition = useCallback(() => {
		const trigger = triggerRef.current;
		if (!trigger) return;

		const rect = trigger.getBoundingClientRect();
		const menuWidth = menuRef.current?.offsetWidth || (compact ? 192 : 224);
		const top = rect.bottom + 6;
		const left = Math.min(
			Math.max(rect.left, 8),
			window.innerWidth - 8 - menuWidth,
		);
		setPosition({ top, left });
	}, [compact]);

	useEffect(() => {
		if (!open) return undefined;

		updatePosition();
		const handlePointerDown = (event) => {
			if (triggerRef.current?.contains(event.target)) return;
			if (menuRef.current?.contains(event.target)) return;
			setOpen(false);
		};
		const handleKeyDown = (event) => {
			if (event.key === "Escape") setOpen(false);
		};
		const handleReposition = () => updatePosition();

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);
		window.addEventListener("resize", handleReposition);
		document.addEventListener("scroll", handleReposition, true);
		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
			window.removeEventListener("resize", handleReposition);
			document.removeEventListener("scroll", handleReposition, true);
		};
	}, [open, updatePosition]);

	const handleReact = async (name) => {
		const reacted = Boolean(awardsByName.get(name)?.currentUserReacted);
		const action = reacted ? onUnreact : onReact;
		if (!canReact || !action) {
			return;
		}

		setLocalError(null);
		try {
			await action(note, name);
			setOpen(false);
		} catch (error) {
			const message =
				error.message ||
				(reacted
					? "Failed to remove emoji reaction"
					: "Failed to add emoji reaction");
			setLocalError(message);
			onError?.(message);
		}
	};

	if (appliedAwards.length === 0 && !canOpenDropdown && !localError) {
		return null;
	}

	const renderReactionButton = (name, source = "inline") => {
		const award = awardsByName.get(name);
		const reacted = Boolean(award?.currentUserReacted);
		const count = award?.count || 0;
		const busy = reactingKey === `${note?.restId || note?.id}:${name}`;
		const preset = EMOJI_REACTION_PRESETS.find(
			(reaction) => reaction.name === name,
		);
		const emoji = EMOJI_BY_NAME[name] || award?.emoji || "🙂";
		const label = preset?.label || name.replace(/_/g, " ");
		const disabled = !canReact || busy || (reacted && !onUnreact);
		const title = reacted
			? `You reacted with ${label}. ${count} total. Click to remove.`
			: `${count} ${label} reaction${count === 1 ? "" : "s"}. Click to react.`;

		if (source === "dropdown") {
			return (
				<button
					key={name}
					type="button"
					onClick={() => handleReact(name)}
					disabled={disabled}
					className={`no-hover-lift flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-xs transition-colors ${
						reacted
							? "bg-[var(--color-accent-muted)] text-[var(--color-text-primary)]"
							: "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
					} disabled:cursor-not-allowed disabled:opacity-60`}
					aria-label={
						reacted ? `Remove your ${label} reaction` : `React with ${label}`
					}
					title={title}
				>
					<span className="w-5 text-base" aria-hidden="true">
						{busy ? "…" : emoji}
					</span>
					<span className="min-w-0 flex-1 truncate">{label}</span>
					{count > 0 && (
						<span className="rounded-full border border-[var(--color-border)] px-1.5 text-[10px] tabular-nums text-[var(--color-text-secondary)]">
							{count}
						</span>
					)}
				</button>
			);
		}

		return (
			<button
				key={name}
				type="button"
				onClick={() => handleReact(name)}
				disabled={disabled}
				className={`inline-flex items-center gap-1 rounded-full border px-1.5 font-medium transition-colors ${
					compact ? "h-6 text-[11px]" : "h-7 text-xs"
				} ${
					reacted
						? "border-[var(--color-accent)] bg-[var(--color-accent-muted)] text-[var(--color-text-primary)]"
						: "border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]"
				} disabled:cursor-not-allowed disabled:opacity-70`}
				aria-label={
					reacted ? `Remove your ${label} reaction` : `React with ${label}`
				}
				title={title}
			>
				<span aria-hidden="true">{busy ? "…" : emoji}</span>
				<span>{count}</span>
			</button>
		);
	};

	const menu =
		open &&
		createPortal(
			<div
				ref={menuRef}
				data-no-row-select
				className="fixed z-[10000] w-56 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-1.5 shadow-xl"
				style={{ top: position.top, left: position.left }}
				onMouseDown={(event) => event.stopPropagation()}
			>
				<div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-tertiary)]">
					React with
				</div>
				{dropdownNames.map((name) => renderReactionButton(name, "dropdown"))}
			</div>,
			document.body,
		);

	return (
		<div className={`mt-2 flex flex-wrap items-center gap-1 ${className}`}>
			{appliedAwards.map((award) => renderReactionButton(award.name))}
			{canOpenDropdown && (
				<div ref={triggerRef} className="relative inline-flex">
					<button
						type="button"
						onClick={() => setOpen((value) => !value)}
						className={`inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-1.5 font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] ${
							compact ? "h-6 text-[11px]" : "h-7 text-xs"
						}`}
						aria-expanded={open}
						aria-haspopup="menu"
						aria-label="Add emoji reaction"
						title="Add emoji reaction"
					>
						<SmilePlus className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
					</button>
					{menu}
				</div>
			)}
			{localError && (
				<span className="basis-full text-xs text-[var(--color-error-text)]">
					{localError}
				</span>
			)}
		</div>
	);
};

const CommentLinkButton = ({
	note,
	mrWebUrl,
	label = "Open comment on GitLab",
	className = "",
}) => {
	const href = getCommentUrl(note, mrWebUrl);
	if (!href) return null;

	return (
		<Tooltip content={label}>
			<a
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				draggable="false"
				className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-link)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] ${className}`}
				aria-label={label}
				title={label}
			>
				<ExternalLink className="h-3.5 w-3.5" />
			</a>
		</Tooltip>
	);
};

const CommentSnippet = ({
	label,
	icon,
	note,
	emptyText,
	onExpand,
	mrWebUrl,
	comments,
}) => {
	const header = (
		<div className="flex items-center justify-between gap-1.5">
			<div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
				{icon}
				{label}
			</div>
			{note && (
				<div className="flex shrink-0 items-center gap-0.5">
					<CommentLinkButton
						note={note}
						mrWebUrl={mrWebUrl}
						className="h-5 w-5"
					/>
					{onExpand && (
						<Tooltip content="Expand comment">
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									onExpand({ note, label, icon });
								}}
								className="inline-flex h-5 w-5 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] transition-colors"
								aria-label={`Expand ${label}`}
								title={`Expand ${label}`}
							>
								<Maximize2 className="w-3 h-3" />
							</button>
						</Tooltip>
					)}
				</div>
			)}
		</div>
	);

	if (!note) {
		return (
			<div className="space-y-1">
				{header}
				<div className="text-xs text-[var(--color-text-secondary)] italic">
					{emptyText}
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-1">
			{header}
			<div className="mr-comment-surface rounded-md p-2">
				<div className="flex items-center justify-between gap-2 mb-1">
					<span className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--color-text-primary)]">
						<Avatar
							src={note.author?.avatarUrl}
							alt={note.author?.name}
							size="sm"
							className="flex-shrink-0"
						/>
						<span className="truncate max-w-[120px]">{note.author?.name}</span>
					</span>
					<span className="text-[10px] text-[var(--color-text-secondary)] whitespace-nowrap">
						<DateTime date={note.createdAt} />
					</span>
				</div>
				<CommentBody
					note={note}
					disableLinks
					className="max-h-32 overflow-y-auto text-xs text-[var(--color-text-secondary)] leading-relaxed"
				/>
				<EmojiReactionBar
					note={note}
					onReact={comments?.addEmojiReaction}
					onUnreact={comments?.removeEmojiReaction}
					canReact={comments?.canReact}
					reactingKey={comments?.reactingEmojiKey}
					compact
				/>
			</div>
		</div>
	);
};

// Small inline modal that lifts a single comment out with full formatting.
const getNoteActionKey = (note) => String(note?.restId || note?.id || "");

const useModalTransition = (isOpen, onClose) => {
	const [closing, setClosing] = useState(false);
	const closeRef = useRef(onClose);
	const timeoutRef = useRef(null);

	useEffect(() => {
		closeRef.current = onClose;
	}, [onClose]);

	useEffect(() => {
		if (!isOpen) return;

		setClosing(false);
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, [isOpen]);

	useEffect(
		() => () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		},
		[],
	);

	const requestClose = useCallback(() => {
		if (closing) return;

		setClosing(true);
		timeoutRef.current = setTimeout(() => {
			timeoutRef.current = null;
			closeRef.current?.();
		}, MODAL_EXIT_MS);
	}, [closing]);

	return { closing, requestClose };
};

const CommentDeleteButton = ({ comments, note, onDeleted }) => {
	if (!comments?.canDeleteNote?.(note)) return null;

	const noteKey = getNoteActionKey(note);
	const deleting =
		comments.deletingNoteId && String(comments.deletingNoteId) === noteKey;

	const handleDelete = async (event) => {
		event.stopPropagation();
		if (deleting) return;
		if (!window.confirm("Delete this comment?")) return;

		try {
			await comments.deleteNote(note);
			onDeleted?.();
		} catch {
			// The hook owns deleteError rendering near the modal footer.
		}
	};

	return (
		<Tooltip content="Delete comment">
			<button
				type="button"
				onClick={handleDelete}
				disabled={deleting}
				className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-60"
				aria-label="Delete comment"
			>
				{deleting ? (
					<Loader2 className="h-3.5 w-3.5 animate-spin" />
				) : (
					<Trash2 className="h-3.5 w-3.5" />
				)}
			</button>
		</Tooltip>
	);
};

const CommentModal = ({ entry, comments, onClose }) => {
	const { closing, requestClose } = useModalTransition(Boolean(entry), onClose);

	useEffect(() => {
		const handleKey = (e) => {
			if (e.key === "Escape") {
				consumeEscapeKey(e);
				requestClose();
			}
		};
		document.addEventListener("keydown", handleKey);
		return () => document.removeEventListener("keydown", handleKey);
	}, [requestClose]);

	if (!entry) return null;
	const { note, label, icon } = entry;

	return createPortal(
		<div
			data-no-row-select
			className={`fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 ${closing ? "modal-backdrop-out" : "modal-backdrop-in"}`}
			onMouseDown={requestClose}
			onDragStart={(e) => e.preventDefault()}
		>
			<div
				className={`bg-[var(--color-bg-primary)] rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col border border-[var(--color-border)] overflow-hidden ${closing ? "modal-pop-out" : "modal-pop-in"}`}
				onMouseDown={(e) => e.stopPropagation()}
			>
				{/* Modal header */}
				<div className="flex items-start justify-between gap-3 px-5 py-3.5 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]/50">
					<div className="flex flex-col gap-2 min-w-0">
						<span className="inline-flex items-center gap-1.5 self-start px-2 py-0.5 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
							{icon}
							{label}
						</span>
						<div className="flex items-center gap-2 min-w-0">
							<span className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-text-primary)] min-w-0">
								<Avatar
									src={note.author?.avatarUrl}
									alt={note.author?.name}
									size="sm"
									className="flex-shrink-0"
								/>
								<span className="truncate">{note.author?.name}</span>
							</span>
							<span className="text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
								<DateTime date={note.createdAt} />
							</span>
						</div>
					</div>
					<div className="flex shrink-0 items-center gap-1">
						<CommentLinkButton note={note} mrWebUrl={comments?.mrWebUrl} />
						<CommentDeleteButton
							comments={comments}
							note={note}
							onDeleted={requestClose}
						/>
						<button
							type="button"
							onClick={requestClose}
							className="p-1 -mr-1 rounded-md text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] transition-colors flex-shrink-0"
							aria-label="Close"
						>
							<X className="w-4 h-4" />
						</button>
					</div>
				</div>
				{/* Modal body */}
				<div className="px-5 py-4 overflow-y-auto">
					<CommentBody
						note={note}
						disableLinks
						className="rb-comment-html-lg text-sm text-[var(--color-text-primary)] leading-relaxed"
					/>
					<EmojiReactionBar
						note={note}
						onReact={comments?.addEmojiReaction}
						onUnreact={comments?.removeEmojiReaction}
						canReact={comments?.canReact}
						reactingKey={comments?.reactingEmojiKey}
						onError={() => {}}
					/>
				</div>
				{/* Modal footer */}
				{(comments?.deleteError ||
					comments?.reactionError ||
					getCommentUrl(note, comments?.mrWebUrl)) && (
					<div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-[var(--color-border)]">
						<div className="min-w-0 text-xs text-[var(--color-error-text)]">
							{comments?.deleteError || comments?.reactionError}
						</div>
						{getCommentUrl(note, comments?.mrWebUrl) && (
							<a
								href={getCommentUrl(note, comments?.mrWebUrl)}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex shrink-0 items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md border border-[var(--color-border)] text-[var(--color-link)] hover:bg-[var(--color-bg-secondary)] transition-colors"
							>
								<ExternalLink className="h-3.5 w-3.5" />
								Open comment
							</a>
						)}
					</div>
				)}
			</div>
		</div>,
		document.body,
	);
};

const ThreadReplyForm = ({ discussionId, comments, onCancel, onSubmitted }) => {
	const [draft, setDraft] = useState("");
	const canSubmit = draft.trim().length > 0 && !comments.replying;

	const submitReply = async (event) => {
		event.preventDefault();
		if (!canSubmit) return;

		try {
			await comments.replyToDiscussion(draft, discussionId);
			setDraft("");
			onSubmitted?.();
		} catch {
			// The hook owns replyError rendering in the modal footer.
		}
	};

	return (
		<form
			onSubmit={submitReply}
			className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-3"
		>
			<textarea
				value={draft}
				onChange={(event) => setDraft(event.target.value)}
				disabled={comments.replying || !comments.canReply}
				placeholder="Reply in this thread..."
				className="min-h-20 w-full resize-y rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-60"
			/>
			<div className="mt-2 flex items-center justify-end gap-2">
				<button
					type="button"
					onClick={onCancel}
					className="inline-flex h-8 items-center rounded-md border border-[var(--color-border)] px-3 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]"
				>
					Cancel
				</button>
				<button
					type="submit"
					disabled={!canSubmit || !comments.canReply}
					className="inline-flex h-8 items-center gap-2 rounded-md bg-[var(--color-action)] px-3 text-sm font-medium text-[var(--color-action-text)] hover:bg-[var(--color-action-hover)] disabled:cursor-not-allowed disabled:opacity-60"
				>
					{comments.replying ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<Send className="h-4 w-4" />
					)}
					Reply
				</button>
			</div>
		</form>
	);
};

const getRenderedCommentCount = (comments, fallbackCount = 0) => {
	const discussions = comments?.discussions || [];
	const notes = comments?.allNotes || [];
	return (
		discussions.reduce(
			(total, discussion) => total + (discussion.notes?.length || 0),
			0,
		) ||
		notes.length ||
		fallbackCount ||
		0
	);
};

const getNoteCreatedTime = (note) => {
	const time = new Date(note?.createdAt || 0).getTime();
	return Number.isFinite(time) ? time : 0;
};

const isAiReviewerNote = (note) =>
	note?.author?.username === AI_CODE_REVIEWER_USERNAME;

const sortNotes = (notes, sortOrder) =>
	[...(notes || [])].sort((a, b) => {
		const diff = getNoteCreatedTime(a) - getNoteCreatedTime(b);
		return sortOrder === "newest" ? -diff : diff;
	});

const sortDiscussions = (discussions, sortOrder) =>
	[...(discussions || [])].sort((a, b) => {
		const diff =
			getNoteCreatedTime(a.notes?.[0]) - getNoteCreatedTime(b.notes?.[0]);
		return sortOrder === "newest" ? -diff : diff;
	});

export const MRCommentsThreadedPanel = ({
	comments,
	className = "",
	compact = false,
	renderDiscussionReference,
	disableBodyLinks = false,
}) => {
	const [replyingToDiscussionId, setReplyingToDiscussionId] = useState(null);
	const [sortOrder, setSortOrder] = useState("oldest");
	const [hideAiReviewer, setHideAiReviewer] = useState(false);

	if (!comments) return null;

	const discussions = comments.discussions || [];
	const notes = comments.allNotes || [];
	const filterNote = (note) => !hideAiReviewer || !isAiReviewerNote(note);
	const visibleDiscussions = sortDiscussions(
		discussions
			.map((discussion) => ({
				...discussion,
				notes: (discussion.notes || []).filter(filterNote),
			}))
			.filter((discussion) => discussion.notes.length > 0),
		sortOrder,
	);
	const visibleNotes = sortNotes(notes.filter(filterNote), sortOrder);
	const renderedCommentCount =
		visibleDiscussions.reduce(
			(total, discussion) => total + (discussion.notes?.length || 0),
			0,
		) || visibleNotes.length;
	const rawCommentCount = getRenderedCommentCount(comments);
	const articleClassName = compact
		? "mr-comment-surface rounded-md p-2"
		: "mr-comment-surface rounded-lg p-3";
	const rootCommentClassName = compact
		? "mr-comment-surface-soft rounded-md p-2"
		: "mr-comment-surface-soft rounded-lg p-3";
	const replyCommentClassName = compact
		? "mr-comment-surface-soft relative ml-5 rounded-md py-2 pl-3"
		: "mr-comment-surface-soft relative ml-7 rounded-lg py-2 pl-4";
	const authorClassName = compact
		? "truncate text-xs font-medium text-[var(--color-text-primary)]"
		: "truncate text-sm font-medium text-[var(--color-text-primary)]";
	const bodyClassName = compact
		? "rb-comment-html text-xs text-[var(--color-text-primary)] leading-relaxed"
		: "rb-comment-html-lg text-sm text-[var(--color-text-primary)] leading-relaxed";
	const spacingClassName = compact ? "space-y-2" : "space-y-3";
	const replyDotClassName = compact
		? "absolute -left-[14px] top-4 h-2 w-2 rounded-full border border-[var(--color-border)] bg-[var(--color-comment-surface)]"
		: "absolute -left-[18px] top-4 h-2 w-2 rounded-full border border-[var(--color-border)] bg-[var(--color-comment-surface)]";
	const replyButtonClassName = compact
		? "inline-flex h-6 items-center gap-1 rounded-md border border-[var(--color-border)] px-2 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] disabled:cursor-not-allowed disabled:opacity-50"
		: "inline-flex h-7 items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] disabled:cursor-not-allowed disabled:opacity-50";
	const renderReactions = (note) => (
		<EmojiReactionBar
			note={note}
			onReact={comments.addEmojiReaction}
			onUnreact={comments.removeEmojiReaction}
			canReact={comments.canReact}
			reactingKey={comments.reactingEmojiKey}
			compact={compact}
		/>
	);

	return (
		<div
			data-no-row-select
			className={className}
			draggable={false}
			onDragStart={(event) => event.stopPropagation()}
		>
			{comments.loading && (
				<div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
					<Loader2 className="h-4 w-4 animate-spin" />
					Loading comments...
				</div>
			)}

			{comments.error && !comments.loading && (
				<div className="rounded-md border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-3 py-2 text-sm text-[var(--color-error-text)]">
					{comments.error}
				</div>
			)}

			{comments.loaded && !comments.loading && renderedCommentCount === 0 && (
				<div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
					{rawCommentCount === 0
						? "No comments yet."
						: "No comments match these comment filters."}
				</div>
			)}

			{comments.loaded && !comments.loading && rawCommentCount > 0 && (
				<div
					className={`sticky top-0 z-20 mb-3 flex flex-wrap items-center justify-between gap-2 border border-[var(--color-border)] bg-[var(--color-bg-primary)] shadow-sm ${
						compact
							? "rounded-md px-2 py-1.5"
							: "-mx-5 rounded-none border-x-0 border-t-0 px-5 py-3"
					}`}
				>
					<div className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-0.5">
						{[
							{ value: "oldest", label: "Oldest" },
							{ value: "newest", label: "Newest" },
						].map((option) => (
							<button
								key={option.value}
								type="button"
								onClick={() => setSortOrder(option.value)}
								aria-pressed={sortOrder === option.value}
								className={`inline-flex h-7 items-center rounded px-2 text-xs font-medium transition-colors ${
									sortOrder === option.value
										? "bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] shadow-sm"
										: "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
								}`}
							>
								{option.label}
							</button>
						))}
					</div>
					<label className="inline-flex h-7 cursor-pointer items-center gap-2 rounded-md border border-[var(--color-border)] px-2 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]">
						<input
							type="checkbox"
							checked={hideAiReviewer}
							onChange={(event) => setHideAiReviewer(event.target.checked)}
							className="h-3.5 w-3.5 accent-[var(--color-action)]"
						/>
						Hide AI reviewer
					</label>
				</div>
			)}

			{visibleDiscussions.length > 0 && (
				<div className={spacingClassName}>
					{visibleDiscussions.map((discussion) => (
						<article key={discussion.id} className={articleClassName}>
							<div className={spacingClassName}>
								{discussion.notes.map((note, index) => {
									const isThreadRoot = index === 0;
									const reference =
										isThreadRoot &&
										typeof renderDiscussionReference === "function"
											? renderDiscussionReference(discussion)
											: null;
									return (
										<div
											key={note.id}
											className={
												isThreadRoot
													? rootCommentClassName
													: replyCommentClassName
											}
										>
											{!isThreadRoot && <span className={replyDotClassName} />}
											<div className="mb-2 flex items-center justify-between gap-3">
												<div className="flex min-w-0 items-center gap-2">
													<Avatar
														src={note.author?.avatarUrl}
														alt={note.author?.name}
														size="sm"
														className="flex-shrink-0"
													/>
													<div className="min-w-0">
														<div className={authorClassName}>
															{note.author?.name}
														</div>
														<div className="truncate text-xs text-[var(--color-text-secondary)]">
															@{note.author?.username || "unknown"}
														</div>
													</div>
													{!isThreadRoot && (
														<span className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]">
															Reply
														</span>
													)}
												</div>
												<div className="flex shrink-0 items-center gap-2 text-xs text-[var(--color-text-secondary)]">
													<DateTime date={note.createdAt} />
													{isThreadRoot && (
														<button
															type="button"
															onClick={() =>
																setReplyingToDiscussionId(discussion.id)
															}
															disabled={!comments.canReply}
															className={replyButtonClassName}
														>
															<Reply className="h-3.5 w-3.5" />
															Reply
														</button>
													)}
													<CommentLinkButton
														note={note}
														mrWebUrl={comments.mrWebUrl}
													/>
													<CommentDeleteButton
														comments={comments}
														note={note}
													/>
												</div>
											</div>
											<CommentBody
												note={note}
												className={bodyClassName}
												disableLinks={disableBodyLinks}
											/>
											{renderReactions(note)}
											{reference && (
												<div className={compact ? "mt-2" : "mt-3"}>
													{reference}
												</div>
											)}
										</div>
									);
								})}
							</div>
							{replyingToDiscussionId === discussion.id && (
								<ThreadReplyForm
									discussionId={discussion.id}
									comments={comments}
									onCancel={() => setReplyingToDiscussionId(null)}
									onSubmitted={() => setReplyingToDiscussionId(null)}
								/>
							)}
						</article>
					))}
				</div>
			)}

			{visibleDiscussions.length === 0 && visibleNotes.length > 0 && (
				<div className={spacingClassName}>
					{visibleNotes.map((note) => (
						<article key={note.id} className={articleClassName}>
							<div className="mb-2 flex items-center justify-between gap-3">
								<div className="flex min-w-0 items-center gap-2">
									<Avatar
										src={note.author?.avatarUrl}
										alt={note.author?.name}
										size="sm"
										className="flex-shrink-0"
									/>
									<div className="min-w-0">
										<div className={authorClassName}>{note.author?.name}</div>
										<div className="truncate text-xs text-[var(--color-text-secondary)]">
											@{note.author?.username || "unknown"}
										</div>
									</div>
								</div>
								<div className="flex shrink-0 items-center gap-2 text-xs text-[var(--color-text-secondary)]">
									<DateTime date={note.createdAt} />
									<CommentLinkButton note={note} mrWebUrl={comments.mrWebUrl} />
									<CommentDeleteButton comments={comments} note={note} />
								</div>
							</div>
							<CommentBody
								note={note}
								className={bodyClassName}
								disableLinks={disableBodyLinks}
							/>
							{renderReactions(note)}
						</article>
					))}
				</div>
			)}

			{(comments.replyError ||
				comments.deleteError ||
				comments.reactionError) && (
				<div className="mt-3 rounded-md border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-3 py-2 text-xs text-[var(--color-error-text)]">
					{comments.replyError ||
						comments.deleteError ||
						comments.reactionError}
				</div>
			)}
		</div>
	);
};

const AllCommentsModal = ({ comments, count = 0, onClose }) => {
	const [draft, setDraft] = useState("");
	const modalOpen = Boolean(comments?.modalOpen);
	const { closing, requestClose } = useModalTransition(modalOpen, onClose);

	useEffect(() => {
		const handleKey = (e) => {
			if (e.key === "Escape") {
				consumeEscapeKey(e);
				requestClose();
			}
		};
		document.addEventListener("keydown", handleKey);
		return () => document.removeEventListener("keydown", handleKey);
	}, [requestClose]);

	if (!modalOpen) return null;

	const renderedCommentCount = getRenderedCommentCount(comments, count);
	const canSubmit = draft.trim().length > 0 && !comments.replying;

	const submitReply = async (event) => {
		event.preventDefault();
		if (!canSubmit) return;

		try {
			await comments.reply(draft);
			setDraft("");
		} catch {
			// The hook owns replyError rendering in the modal footer.
		}
	};

	return createPortal(
		<div
			data-no-row-select
			className={`fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 ${closing ? "modal-backdrop-out" : "modal-backdrop-in"}`}
			onMouseDown={requestClose}
			onDragStart={(e) => e.preventDefault()}
		>
			<div
				className={`bg-[var(--color-bg-primary)] rounded-xl shadow-2xl w-full max-w-3xl max-h-[86vh] flex flex-col border border-[var(--color-border)] overflow-hidden ${closing ? "modal-pop-out" : "modal-pop-in"}`}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]/50">
					<div className="min-w-0">
						<h2 className="truncate text-base font-semibold text-[var(--color-text-primary)]">
							Comments
						</h2>
						<p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
							{comments.loading
								? "Loading..."
								: `${renderedCommentCount || count || 0} comment${(renderedCommentCount || count || 0) === 1 ? "" : "s"}`}
						</p>
					</div>
					<div className="flex shrink-0 items-center gap-2">
						{safeHttpUrl(comments.mrWebUrl) && (
							<a
								href={safeHttpUrl(comments.mrWebUrl)}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2.5 text-xs font-medium text-[var(--color-link)] hover:bg-[var(--color-bg-hover)]"
							>
								<ExternalLink className="h-3.5 w-3.5" />
								Open MR
							</a>
						)}
						<button
							type="button"
							onClick={requestClose}
							className="p-1 -mr-1 rounded-md text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] transition-colors flex-shrink-0"
							aria-label="Close comments"
						>
							<X className="w-4 h-4" />
						</button>
					</div>
				</div>

				<div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">
					<MRCommentsThreadedPanel comments={comments} disableBodyLinks />
				</div>

				<form
					onSubmit={submitReply}
					className="border-t border-[var(--color-border)] bg-[var(--color-bg-secondary)]/50 px-5 py-4"
				>
					<textarea
						value={draft}
						onChange={(event) => setDraft(event.target.value)}
						disabled={comments.replying || !comments.canReply}
						placeholder={
							comments.canReply
								? "Reply to this merge request..."
								: "Reply unavailable for this merge request"
						}
						className="min-h-24 w-full resize-y rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-60"
					/>
					<div className="mt-2 flex items-center justify-between gap-3">
						<div className="min-w-0 text-xs text-[var(--color-error-text)]">
							{comments.replyError ||
								comments.deleteError ||
								comments.reactionError}
						</div>
						<button
							type="submit"
							disabled={!canSubmit || !comments.canReply}
							className="inline-flex h-8 items-center gap-2 rounded-md bg-[var(--color-action)] px-3 text-sm font-medium text-[var(--color-action-text)] hover:bg-[var(--color-action-hover)] disabled:cursor-not-allowed disabled:opacity-60"
						>
							{comments.replying ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Send className="h-4 w-4" />
							)}
							Reply
						</button>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	);
};

const iconClass = "w-3.5 h-3.5 text-[var(--color-text-secondary)]";

/**
 * Collapsible "Show comments" control that lazily loads and displays the
 * latest AI reviewer, Murloc, Atlantis and human comments for a merge request.
 * Reusable across the Standard, Swimlane and Review Board card layouts.
 */
export const useMRComments = (mrOrId) => {
	const [expanded, setExpanded] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalEntry, setModalEntry] = useState(null);
	const {
		load,
		loading,
		error,
		loaded,
		discussions,
		allNotes,
		aiNote,
		murlocNote,
		atlantisNote,
		humanNote,
		reply,
		replyToDiscussion,
		replying,
		replyError,
		canReply,
		deleteNote,
		canDeleteNote,
		deletingNoteId,
		deleteError,
		addEmojiReaction,
		removeEmojiReaction,
		canReact,
		reactingEmojiKey,
		reactionError,
		mrWebUrl,
	} = useMRNotes(mrOrId);
	const mrId = typeof mrOrId === "object" ? mrOrId?.id : mrOrId;

	useEffect(() => {
		if (mrId && expanded && !loaded && !loading) {
			load();
		}
	}, [expanded, loaded, loading, load, mrId]);

	useEffect(() => {
		if (mrId && modalOpen && !loaded && !loading) {
			load();
		}
	}, [loaded, loading, load, modalOpen, mrId]);

	const openModal = () => {
		setModalOpen(true);
		if (mrId && !loaded && !loading) {
			load();
		}
	};

	return {
		expanded,
		setExpanded,
		toggle: () => setExpanded((prev) => !prev),
		modalOpen,
		openModal,
		closeModal: () => setModalOpen(false),
		loading,
		error,
		loaded,
		discussions,
		allNotes,
		aiNote,
		murlocNote,
		atlantisNote,
		humanNote,
		reply,
		replyToDiscussion,
		replying,
		replyError,
		canReply,
		deleteNote,
		canDeleteNote,
		deletingNoteId,
		deleteError,
		addEmojiReaction,
		removeEmojiReaction,
		canReact,
		reactingEmojiKey,
		reactionError,
		mrWebUrl,
		modalEntry,
		setModalEntry,
	};
};

const MRCommentsFullToggle = ({ comments }) => (
	<button
		type="button"
		onClick={(e) => {
			e.stopPropagation();
			comments.toggle();
		}}
		className="w-full inline-flex items-center justify-center gap-1 text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] py-1 rounded hover:bg-[var(--color-bg-secondary)] transition-colors"
		aria-expanded={comments.expanded}
	>
		{comments.expanded ? (
			<ChevronUp className="w-3.5 h-3.5" />
		) : (
			<ChevronDown className="w-3.5 h-3.5" />
		)}
		{comments.expanded ? "Hide comments" : "Show comments"}
	</button>
);

export const MRCommentsIconButton = ({
	comments,
	count = 0,
	className = "",
}) => {
	if (!comments) return null;

	const label = "Open comments";

	return (
		<Tooltip content={label}>
			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					comments.openModal();
				}}
				className={`relative inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] ${
					comments.modalOpen
						? "bg-[var(--color-accent-muted)] text-[var(--color-text-primary)]"
						: ""
				} ${className}`}
				aria-haspopup="dialog"
				aria-label={label}
			>
				<MessageSquare className="h-4 w-4" />
				{count > 0 && (
					<span className="absolute -right-1 -top-1 flex min-w-3.5 items-center justify-center rounded-full bg-[var(--color-action)] px-0.5 text-[9px] font-semibold leading-3.5 text-[var(--color-action-text)]">
						{count > 99 ? "99+" : count}
					</span>
				)}
			</button>
		</Tooltip>
	);
};

export const MRCommentsPanel = ({ comments, className = "" }) => {
	if (!comments) return null;
	if (!comments.expanded && !comments.modalEntry && !comments.modalOpen) {
		return null;
	}

	return (
		<div className={className} data-no-row-select>
			{/* Unfurled comments section */}
			{comments.expanded && (
				<div
					className="space-y-2"
					draggable={false}
					onDragStart={(e) => e.stopPropagation()}
				>
					{comments.loading && (
						<div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] py-2">
							<Loader2 className="w-3.5 h-3.5 animate-spin" />
							Loading comments...
						</div>
					)}

					{comments.error && !comments.loading && (
						<div className="text-xs text-red-500 py-1">{comments.error}</div>
					)}

					{comments.loaded && !comments.loading && (
						<>
							<CommentSnippet
								label="AI Review"
								icon={<Bot className={iconClass} />}
								note={comments.aiNote}
								emptyText="No AI Reviewer comment yet."
								onExpand={comments.setModalEntry}
								mrWebUrl={comments.mrWebUrl}
								comments={comments}
							/>
							{comments.murlocNote && (
								<CommentSnippet
									label="Murloc"
									icon={<Fish className={iconClass} />}
									note={comments.murlocNote}
									onExpand={comments.setModalEntry}
									mrWebUrl={comments.mrWebUrl}
									comments={comments}
								/>
							)}
							{comments.atlantisNote && (
								<CommentSnippet
									label="Atlantis (policy)"
									icon={<ShieldCheck className={iconClass} />}
									note={comments.atlantisNote}
									onExpand={comments.setModalEntry}
									mrWebUrl={comments.mrWebUrl}
									comments={comments}
								/>
							)}
							<CommentSnippet
								label="Last human comment"
								icon={<MessageSquare className={iconClass} />}
								note={comments.humanNote}
								emptyText="No human comments yet."
								onExpand={comments.setModalEntry}
								mrWebUrl={comments.mrWebUrl}
								comments={comments}
							/>
						</>
					)}
				</div>
			)}

			<CommentModal
				entry={comments.modalEntry}
				comments={comments}
				onClose={() => comments.setModalEntry(null)}
			/>
			<AllCommentsModal
				comments={comments}
				count={comments.allNotes?.length}
				onClose={comments.closeModal}
			/>
		</div>
	);
};

export const MRComments = ({ mrId, className = "" }) => {
	const comments = useMRComments(mrId);

	return (
		<div className={className}>
			<MRCommentsFullToggle comments={comments} />
			<MRCommentsPanel comments={comments} className="mt-2" />
		</div>
	);
};
